import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FormatData } from '@classes/format-data';
import { Order, LogElement } from '@models/order';
import { OrderService } from '@services/order.service';
import { PatientService } from '@services/patient.service';
import { ProductService } from '@services/product.service';

export class ExchangeJob {
  static nextXInvoice(originalInvoice: string, orders: Order[]): string {
    const root = FormatData.invoiceFamilyRoot(originalInvoice);
    if (!root) {
      return '';
    }
    const escaped = root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-X(\\d+)?$`, 'i');
    let max = 0;
    (orders || []).forEach((order) => {
      const invoice = String(order?.invoiceNumber || '').trim();
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (max === 0) {
      return `${root}-X`;
    }
    return `${root}-X${max + 1}`;
  }

  static buildLinkedExchangeOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    financial: { credit: number; restockingFee: number; netCredit: number },
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.exchangeCredit = financial.netCredit || 0;
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.vendorOrder = null;
    clone.rxStatus = '';
    clone.eta = '';
    clone.archived = false;
    clone.framesRemoved = [];
    clone.tax = 0;
    clone.shipping = 0;
    clone.total = 0;
    clone.frameOnly = false;
    clone.frameOnlyTaxCredit = 0;
    clone.fulfillment = 'PICKUP';
    clone.status = '';
    clone.todo = 'SAVE';
    clone.jobAction = '';
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.tax = 0;
      productElement.shipping = 0;
      productElement.cost = 0;
      productElement.retail = 0;
      productElement.destination = '';
      const lensPrice = Number(productElement.lens?.price) || 0;
      productElement.subtotal = lensPrice;

      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = false;
      productElement.frame.deposit = 0;
      productElement.frame.enclosed = false;
      productElement.frame.physicalStatus = '';
      productElement.frame.preOrderStatus = '';
      productElement.frame.activity = [];
      productElement.frame.activityStatus = '';
      productElement.frame.dateReceived = '';
      productElement.frame.followUpDate = '';
      productElement.frame.followUpStatus = '';
      productElement.frame.referenceNumber = '';
      productElement.frame.labVendor = '';
      productElement.frame.carrier = '';
      productElement.frame.trackingNumber = '';
      productElement.frame.shipDate = '';
      productElement.frame.repairItems = [];
      productElement.frame.orderDate = '';
      productElement.frame.failedInspection = false;

      if (productElement.lens) {
        productElement.lens.labReferenceNumber = '';
        productElement.lens.POLLabReferenceNumber = '';
        productElement.lens.date = '';
        productElement.lens.failedInspection = '';
      }
    });

    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const exchangeLog = new LogElement();
    exchangeLog.initials = context.initials;
    exchangeLog.user = context.userId;
    exchangeLog.item = originalInvoice
      ? `EXCHANGE FROM ${originalInvoice}`
      : 'EXCHANGE';
    const creditNotes = [
      financial.restockingFee > 0
        ? `CREDIT ${FormatData.formatCurrency(financial.credit)}; FEE ${FormatData.formatCurrency(financial.restockingFee)}; NET ${FormatData.formatCurrency(financial.netCredit)}`
        : `FRAME CREDIT ${FormatData.formatCurrency(financial.netCredit)}`,
      'ORIGINAL FRAME RETURNED TO INVENTORY',
      'SELECT REPLACEMENT FRAME AT CURRENT PRICING',
    ];
    exchangeLog.notes = creditNotes.join('; ');
    exchangeLog.date = Date.now().toString();
    clone.log = [created, exchangeLog];

    return clone as Order;
  }

  static create$(
    original: any,
    originalInvoice: string,
    existingOrders: Order[],
    financial: { credit: number; restockingFee: number; netCredit: number },
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    },
    services: {
      orderService: OrderService;
      productService: ProductService;
      patientService: PatientService;
    }
  ): Observable<Order> {
    const invoiceNumber = ExchangeJob.nextXInvoice(
      originalInvoice,
      existingOrders
    );
    const newOrder = ExchangeJob.buildLinkedExchangeOrder(
      original,
      originalInvoice,
      invoiceNumber,
      financial,
      context
    );
    const product = original?.products?.[0]?.product;
    const quantity = parseInt(original?.products?.[0]?.quantity, 10) || 1;

    return services.orderService.addOrder(newOrder).pipe(
      switchMap((createdOrders: Order[]) => {
        const created = Array.isArray(createdOrders)
          ? createdOrders[0]
          : createdOrders;
        const restoreInventory$ =
          product?._id && product._id !== 'NEW'
            ? services.productService
                .incrementProductInventoryQuantity(product, Math.abs(quantity))
                .pipe(catchError(() => of(null)))
            : of(null);
        const credit$ =
          financial.netCredit > 0
            ? services.patientService
                .incrementStoreCredit(context.patientId, financial.netCredit)
                .pipe(catchError(() => of(null)))
            : of(null);
        return restoreInventory$.pipe(switchMap(() => credit$), map(() => created));
      })
    );
  }
}
